/**
 * Nexsus+ Rewards — Storefront Loyalty Widget & Points Messaging
 * 
 * Features:
 *  1. Floating Rewards Launcher with points badge
 *  2. Slide-out panel: Rewards (with direct redeem & discount code copy),
 *     Activity history, My Codes wallet, Challenges/Missions with progress,
 *     Active Weekly Prize Draw entry, and Referral sharing
 *  3. Dynamic Product Page Points Messaging ("Earn X points with this item")
 *  4. Dynamic Cart Points Messaging ("You'll earn X points on this order")
 */
(function () {
  "use strict";

  const PROXY = "/apps/nexsus-rewards";
  const PREFIX = "nxr";
  const TIER_ORDER = ["BRONZE", "SILVER", "GOLD", "VIP"];
  const TIER_COLORS = { BRONZE: "#b45309", SILVER: "#71717a", GOLD: "#d97706", VIP: "#6f263d" };

  let state = {
    open: false,
    loading: false,
    data: null,
    settings: { pointsPerPound: 10, pointsName: "Points", productPointsEnabled: true, cartPointsEnabled: true },
    error: null,
    redeemingId: null,
    enteringDraw: false,
    redeemedCodes: {},
    copiedKey: null,
    tab: "rewards",
  };

  const esc = (s) =>
    String(s ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");

  const pts = (v) => Number(v || 0).toLocaleString();

  function formatDate(d) {
    if (!d) return "";
    const dt = new Date(d);
    return dt.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
  }

  function injectStyles() {
    if (document.getElementById(`${PREFIX}-styles`)) return;
    const style = document.createElement("style");
    style.id = `${PREFIX}-styles`;
    style.textContent = `
      :root{
        --nxr-burgundy:#6F263D;
        --nxr-burgundy-dark:#541C2D;
        --nxr-burgundy-glow:rgba(111,38,61,0.28);
        --nxr-ink:#171717;
        --nxr-warm:#FAF9F7;
        --nxr-stone:#F5F1EA;
        --nxr-border:#E7E4DF;
        --nxr-gold:#D4A017;
        --nxr-ease-out:cubic-bezier(0.16, 1, 0.3, 1);
        --nxr-ease-spring:cubic-bezier(0.34, 1.56, 0.64, 1);
      }
      @keyframes nxrPulseGlow{
        0%,100%{box-shadow:0 6px 20px rgba(23,23,23,.28), 0 0 0 0 rgba(111,38,61,0)}
        50%{box-shadow:0 8px 24px rgba(23,23,23,.32), 0 0 0 7px rgba(111,38,61,.22)}
      }
      @keyframes nxrShimmer{
        0%{background-position:-200% 0}
        100%{background-position:200% 0}
      }
      @keyframes nxrCardIn{
        from{opacity:0;transform:translateY(14px) scale(0.98)}
        to{opacity:1;transform:translateY(0) scale(1)}
      }
      @keyframes nxrFadeIn{
        from{opacity:0;transform:translateY(6px)}
        to{opacity:1;transform:translateY(0)}
      }
      @keyframes nxrBadgePop{
        0%{transform:scale(0.4);opacity:0}
        70%{transform:scale(1.15)}
        100%{transform:scale(1);opacity:1}
      }
      @keyframes nxrWiggle{
        0%,100%{transform:rotate(0deg)}
        25%{transform:rotate(-12deg) scale(1.1)}
        75%{transform:rotate(12deg) scale(1.1)}
      }
      @keyframes nxrShineSweep{
        0%{background-position:200% 0}
        100%{background-position:-200% 0}
      }

      .${PREFIX}-launcher{
        position:fixed;bottom:24px;right:24px;z-index:999990;
        height:48px;padding:0 20px 0 16px;border-radius:26px;
        background:#171717;color:#fff;
        border:1.5px solid rgba(111,38,61,.45);
        cursor:pointer;
        box-shadow:0 6px 20px rgba(23,23,23,.28);
        display:inline-flex;align-items:center;gap:9px;
        font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;
        font-size:13px;font-weight:700;letter-spacing:.02em;
        transition:transform .28s var(--nxr-ease-out),box-shadow .28s var(--nxr-ease-out),background .2s ease,border-color .2s ease;
        animation:nxrPulseGlow 4s infinite ease-in-out;
        will-change:transform,box-shadow;
      }
      .${PREFIX}-launcher:hover{
        transform:translateY(-3px) scale(1.02);
        background:#24191e;border-color:#6F263D;
        box-shadow:0 12px 28px rgba(111,38,61,.4);
        animation:none;
      }
      .${PREFIX}-launcher:active{transform:translateY(-1px) scale(0.99)}
      .${PREFIX}-launcher:hover .${PREFIX}-launcher-icon{animation:nxrWiggle .4s ease-in-out}
      .${PREFIX}-launcher-icon{font-size:18px;line-height:1;display:inline-block;transition:transform .2s}
      .${PREFIX}-launcher-text{display:inline-block;white-space:nowrap}
      .${PREFIX}-launcher-badge{
        position:absolute;top:-6px;right:-4px;
        background:linear-gradient(135deg,#6F263D 0%,#8E2B4B 100%);
        color:#fff;font-size:11px;font-weight:800;
        padding:2px 7px;border-radius:12px;min-width:18px;
        text-align:center;line-height:1.3;
        border:2px solid #fff;
        box-shadow:0 3px 8px rgba(0,0,0,.3);
        animation:nxrBadgePop .4s var(--nxr-ease-spring) backwards;
      }

      .${PREFIX}-overlay{
        position:fixed;inset:0;
        background:rgba(23,23,23,.55);
        backdrop-filter:blur(8px);-webkit-backdrop-filter:blur(8px);
        z-index:999991;opacity:0;visibility:hidden;
        transition:opacity .35s var(--nxr-ease-out),visibility .35s;
        pointer-events:none;
      }
      .${PREFIX}-overlay.${PREFIX}-open{
        opacity:1;visibility:visible;pointer-events:auto;
      }

      .${PREFIX}-panel{
        position:fixed;top:0;right:0;bottom:0;
        width:420px;max-width:100vw;background:#fff;z-index:999992;
        transform:translate3d(105%,0,0);
        transition:transform .4s var(--nxr-ease-out);
        display:flex;flex-direction:column;
        font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;
        color:#171717;overflow:hidden;
        box-shadow:-12px 0 40px rgba(0,0,0,.22);
        will-change:transform;
      }
      .${PREFIX}-panel.${PREFIX}-open{
        transform:translate3d(0,0,0);
      }

      .${PREFIX}-panel-head{
        padding:18px 22px 15px;
        border-bottom:1px solid #E7E4DF;
        display:flex;justify-content:space-between;align-items:center;
        flex-shrink:0;background:#fff;
        transition:box-shadow .25s ease,border-color .25s ease;
        z-index:2;position:relative;
      }
      .${PREFIX}-panel-head.${PREFIX}-scrolled{
        box-shadow:0 4px 18px rgba(0,0,0,.07);
        border-bottom-color:transparent;
      }
      .${PREFIX}-panel-head h2{
        margin:0;font-size:17px;font-weight:750;letter-spacing:-.015em;color:#171717;
        display:flex;align-items:center;gap:7px;
      }
      .${PREFIX}-close{
        background:rgba(0,0,0,.04);border:none;width:32px;height:32px;border-radius:16px;
        font-size:20px;cursor:pointer;color:#666;
        display:inline-flex;align-items:center;justify-content:center;
        padding:0;line-height:1;transition:all .18s var(--nxr-ease-out);
      }
      .${PREFIX}-close:hover{background:#6F263D;color:#fff;transform:scale(1.06)}
      .${PREFIX}-close:active{transform:scale(0.95)}

      .${PREFIX}-body{
        flex:1;overflow-y:auto;padding:0;
        -webkit-overflow-scrolling:touch;
        scrollbar-width:thin;
        scrollbar-color:rgba(111,38,61,.25) transparent;
      }
      .${PREFIX}-body::-webkit-scrollbar{width:6px}
      .${PREFIX}-body::-webkit-scrollbar-track{background:transparent}
      .${PREFIX}-body::-webkit-scrollbar-thumb{background:rgba(111,38,61,.25);border-radius:3px}
      .${PREFIX}-body::-webkit-scrollbar-thumb:hover{background:rgba(111,38,61,.45)}

      .${PREFIX}-section{padding:18px 22px;border-bottom:1px solid #F0ECE6}
      .${PREFIX}-section:last-child{border-bottom:none}

      .${PREFIX}-hero{
        text-align:center;padding:26px 22px 22px;
        background:linear-gradient(180deg,#FAF9F7 0%,#F5F1EA 100%);
        border-bottom:1px solid #E7E4DF;
        position:relative;overflow:hidden;
      }
      .${PREFIX}-hero-points{
        font-size:44px;font-weight:800;margin:0;line-height:1.02;
        color:#171717;letter-spacing:-.025em;
        font-feature-settings:"tnum";font-variant-numeric:tabular-nums;
        transition:color .3s;
      }
      .${PREFIX}-hero-label{font-size:13px;color:#6B6B6B;margin:6px 0 0;font-weight:550}
      .${PREFIX}-hero-pending{font-size:12px;color:#8E2B4B;margin:4px 0 0;font-weight:600}
      .${PREFIX}-tier-badge{
        display:inline-block;padding:4px 14px;border-radius:14px;
        font-size:11px;font-weight:800;letter-spacing:.08em;
        color:#fff;margin-top:10px;text-transform:uppercase;
        box-shadow:0 3px 8px rgba(0,0,0,.14);
      }
      .${PREFIX}-progress-wrap{margin-top:16px}
      .${PREFIX}-progress-bar{height:7px;background:rgba(0,0,0,.08);border-radius:4px;overflow:hidden}
      .${PREFIX}-progress-fill{
        height:100%;background:linear-gradient(90deg,#6F263D 0%,#D4A017 100%);
        border-radius:4px;transition:width .8s var(--nxr-ease-out);
      }
      .${PREFIX}-progress-label{
        display:flex;justify-content:space-between;
        font-size:11px;color:#777;margin-top:6px;font-weight:550;
      }

      .${PREFIX}-tabs{
        display:flex;border-bottom:1px solid #E7E4DF;
        flex-shrink:0;overflow-x:auto;background:#fff;
        position:sticky;top:0;z-index:1;
      }
      .${PREFIX}-tab{
        flex:1;padding:13px 8px;text-align:center;
        font-size:12px;font-weight:700;color:#777;
        background:none;border:none;border-bottom:2px solid transparent;
        cursor:pointer;white-space:nowrap;
        transition:color .2s var(--nxr-ease-out),border-color .2s var(--nxr-ease-out),background .2s;
      }
      .${PREFIX}-tab:hover{color:#6F263D;background:rgba(111,38,61,.03)}
      .${PREFIX}-tab.${PREFIX}-active{
        color:#6F263D;border-bottom-color:#6F263D;
      }

      .${PREFIX}-card-anim{
        animation:nxrCardIn .38s var(--nxr-ease-out) backwards;
      }

      .${PREFIX}-reward-card{
        padding:15px 0;border-bottom:1px solid #F0ECE6;
        display:flex;justify-content:space-between;align-items:center;gap:12px;
        transition:transform .2s ease;
      }
      .${PREFIX}-reward-card:last-child{border-bottom:none}
      .${PREFIX}-reward-info{flex:1;min-width:0}
      .${PREFIX}-reward-name{font-size:14px;font-weight:700;margin:0;color:#171717}
      .${PREFIX}-reward-desc{font-size:12px;color:#6B6B6B;margin:3px 0 0;line-height:1.35}
      .${PREFIX}-reward-cost{font-size:12px;color:#6F263D;font-weight:700;margin:4px 0 0}
      .${PREFIX}-reward-btn{
        padding:9px 18px;border-radius:7px;
        font-size:12px;font-weight:700;border:none;
        cursor:pointer;white-space:nowrap;
        transition:all .2s var(--nxr-ease-out);
      }
      .${PREFIX}-reward-btn-redeem{
        background:#171717;color:#fff;
        box-shadow:0 3px 8px rgba(23,23,23,.18);
      }
      .${PREFIX}-reward-btn-redeem:hover{
        background:#6F263D;transform:translateY(-1px);
        box-shadow:0 6px 14px rgba(111,38,61,.3);
      }
      .${PREFIX}-reward-btn-redeem:active{transform:scale(0.97)}
      .${PREFIX}-reward-btn-redeem:disabled{opacity:.55;cursor:not-allowed;transform:none}
      .${PREFIX}-reward-btn-locked{background:#F5F1EA;color:#999;cursor:default}

      .${PREFIX}-code-box{
        margin-top:10px;padding:11px 14px;
        background:#FAF9F7;border:1px dashed #6F263D;
        border-radius:8px;display:flex;align-items:center;
        justify-content:space-between;gap:8px;
        animation:nxrFadeIn .3s var(--nxr-ease-out);
      }
      .${PREFIX}-code-text{
        font-family:'Courier New',Consolas,monospace;
        font-size:14px;font-weight:800;color:#171717;letter-spacing:.05em;
      }
      .${PREFIX}-code-copy{
        padding:6px 14px;font-size:11px;font-weight:700;
        background:#171717;color:#fff;border:none;border-radius:6px;
        cursor:pointer;transition:all .2s var(--nxr-ease-out);
      }
      .${PREFIX}-code-copy:hover{background:#6F263D;transform:scale(1.04)}
      .${PREFIX}-code-copy:active{transform:scale(0.94)}
      .${PREFIX}-code-copy.${PREFIX}-copied{background:#16a34a!important}

      .${PREFIX}-card{
        padding:15px;border:1px solid #E7E4DF;border-radius:10px;
        margin-bottom:12px;background:#FAF9F7;
        transition:transform .2s var(--nxr-ease-out),box-shadow .2s ease;
      }
      .${PREFIX}-card:hover{
        transform:translateY(-1px);
        box-shadow:0 4px 12px rgba(0,0,0,.05);
      }
      .${PREFIX}-card-title{font-size:14px;font-weight:700;margin:0 0 4px;color:#171717}
      .${PREFIX}-card-desc{font-size:12px;color:#6B6B6B;margin:0;line-height:1.4}
      .${PREFIX}-card-badge{
        display:inline-block;padding:3px 9px;background:#6F263D;color:#fff;
        font-size:11px;font-weight:700;border-radius:6px;margin-top:8px;
      }

      .${PREFIX}-draw-box{
        text-align:center;padding:22px 18px;border:1px solid #E7E4DF;
        border-radius:12px;background:linear-gradient(180deg,#FAF9F7 0%,#F5F1EA 100%);
        box-shadow:0 4px 14px rgba(0,0,0,.04);
      }
      .${PREFIX}-draw-btn{
        display:block;width:100%;margin-top:14px;padding:12px;
        background:#171717;color:#fff;font-weight:800;font-size:13px;
        border:none;border-radius:7px;cursor:pointer;letter-spacing:.03em;
        transition:all .25s var(--nxr-ease-out);
        box-shadow:0 4px 12px rgba(23,23,23,.2);
      }
      .${PREFIX}-draw-btn:hover{
        background:#6F263D;transform:translateY(-2px);
        box-shadow:0 8px 20px rgba(111,38,61,.35);
      }
      .${PREFIX}-draw-btn:active{transform:scale(0.98)}
      .${PREFIX}-draw-btn:disabled{opacity:.55;cursor:not-allowed;transform:none}

      .${PREFIX}-tx-row{
        display:flex;justify-content:space-between;align-items:center;
        padding:11px 0;border-bottom:1px solid #F0ECE6;font-size:13px;
      }
      .${PREFIX}-tx-pts{font-weight:700;font-size:13px}
      .${PREFIX}-tx-pts.${PREFIX}-pos{color:#16a34a}
      .${PREFIX}-tx-pts.${PREFIX}-neg{color:#dc2626}

      .${PREFIX}-referral-box{
        padding:22px 20px;
        background:linear-gradient(135deg,#171717 0%,#2e1921 100%);
        color:#fff;border-radius:12px;text-align:center;
        border:1px solid rgba(111,38,61,.45);
        box-shadow:0 6px 20px rgba(0,0,0,.15);
      }
      .${PREFIX}-referral-code{
        font-family:'Courier New',Consolas,monospace;
        font-size:23px;font-weight:800;letter-spacing:.14em;
        margin:12px 0;color:#FAF9F7;
      }
      .${PREFIX}-referral-copy{
        padding:9px 22px;font-size:12px;font-weight:750;
        background:#6F263D;color:#fff;border:none;border-radius:7px;
        cursor:pointer;transition:all .2s var(--nxr-ease-out);
        box-shadow:0 3px 10px rgba(111,38,61,.3);
      }
      .${PREFIX}-referral-copy:hover{
        background:#8E2B4B;transform:translateY(-1px) scale(1.02);
      }
      .${PREFIX}-referral-copy:active{transform:scale(0.96)}

      .${PREFIX}-toast{
        position:fixed;bottom:90px;right:24px;z-index:999995;
        background:#171717;color:#fff;padding:12px 22px;border-radius:9px;
        font-size:13px;font-weight:600;
        box-shadow:0 8px 24px rgba(0,0,0,.3);
        opacity:0;transform:translateY(12px) scale(0.96);
        transition:opacity .25s var(--nxr-ease-out),transform .25s var(--nxr-ease-out);
        pointer-events:none;border-left:4px solid #6F263D;
      }
      .${PREFIX}-toast.${PREFIX}-show{opacity:1;transform:translateY(0) scale(1)}

      .${PREFIX}-product-pill{
        display:inline-flex;align-items:center;gap:8px;
        padding:9px 15px;background:#FAF9F7;
        background-image:linear-gradient(110deg,transparent 20%,rgba(111,38,61,0.08) 50%,transparent 80%);
        background-size:200% 100%;
        border:1px solid #E7E4DF;border-radius:7px;
        color:#171717;font-size:13px;font-weight:550;
        margin:10px 0 14px;cursor:pointer;
        transition:all .25s var(--nxr-ease-out);width:fit-content;
        animation:nxrShineSweep 6s infinite linear;
      }
      .${PREFIX}-product-pill:hover{
        background-color:#F5F1EA;border-color:#6F263D;
        transform:translateY(-1px);
        box-shadow:0 4px 12px rgba(111,38,61,.12);
      }
      .${PREFIX}-product-pill:active{transform:scale(0.98)}
      .${PREFIX}-product-pill strong{color:#6F263D;font-weight:750}
      .${PREFIX}-product-pill svg{width:16px;height:16px;flex-shrink:0;fill:#6F263D;transition:transform .2s}
      .${PREFIX}-product-pill:hover svg{transform:rotate(12deg) scale(1.15)}

      .${PREFIX}-cart-banner{
        display:flex;align-items:center;gap:11px;
        padding:13px 18px;background:#FAF9F7;
        border:1px solid #E7E4DF;border-left:4px solid #6F263D;
        border-radius:8px;color:#171717;font-size:13px;font-weight:500;
        margin:14px 0;animation:nxrFadeIn .35s var(--nxr-ease-out);
      }
      .${PREFIX}-cart-banner strong{color:#6F263D;font-weight:750}

      /* Shimmer Skeleton */
      .${PREFIX}-skeleton-wrap{padding:20px;display:flex;flex-direction:column;gap:16px}
      .${PREFIX}-skeleton-hero{
        background:linear-gradient(90deg,#eee 25%,#f5f5f5 50%,#eee 75%);
        background-size:200% 100%;animation:nxrShimmer 1.5s infinite;
        height:130px;border-radius:10px;margin-bottom:10px;
      }
      .${PREFIX}-skeleton-line{
        background:linear-gradient(90deg,#eee 25%,#f5f5f5 50%,#eee 75%);
        background-size:200% 100%;animation:nxrShimmer 1.5s infinite;
        height:16px;border-radius:4px;
      }
      .${PREFIX}-skeleton-card{
        background:linear-gradient(90deg,#eee 25%,#f5f5f5 50%,#eee 75%);
        background-size:200% 100%;animation:nxrShimmer 1.5s infinite;
        height:56px;border-radius:8px;
      }

      /* Canvas Confetti */
      #${PREFIX}-confetti-canvas{
        position:fixed;inset:0;width:100vw;height:100vh;
        pointer-events:none;z-index:999999;
      }

      @media(max-width:920px){
        .${PREFIX}-launcher{bottom:82px;right:14px;height:44px;padding:0 15px 0 12px;font-size:12px}
      }
      @media(max-width:440px){
        .${PREFIX}-panel{width:100vw;max-width:100vw}
        .${PREFIX}-launcher{bottom:84px;right:12px}
      }

      @media(prefers-reduced-motion:reduce){
        *,::before,::after{
          animation-duration:0.01ms!important;
          animation-iteration-count:1!important;
          transition-duration:0.01ms!important;
        }
      }
    `;
    document.head.appendChild(style);
  }

  let toastTimer = null;
  function showToast(msg) {
    let el = document.getElementById(`${PREFIX}-toast`);
    if (!el) {
      el = document.createElement("div");
      el.id = `${PREFIX}-toast`;
      el.className = `${PREFIX}-toast`;
      document.body.appendChild(el);
    }
    el.textContent = msg;
    el.classList.add(`${PREFIX}-show`);
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => el.classList.remove(`${PREFIX}-show`), 2400);
  }

  async function copyText(text, key) {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.style.cssText = "position:fixed;opacity:0";
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      ta.remove();
    }
    state.copiedKey = key;
    render();
    showToast("Copied to clipboard!");
    setTimeout(() => {
      if (state.copiedKey === key) {
        state.copiedKey = null;
        render();
      }
    }, 2000);
  }

  async function fetchSettings() {
    try {
      const r = await fetch(`${PROXY}?view=settings`);
      if (r.ok) {
        const s = await r.json();
        state.settings = { ...state.settings, ...s };
      }
    } catch {}
  }

  async function fetchData() {
    state.loading = true;
    state.error = null;
    render();
    try {
      const r = await fetch(`${PROXY}?view=widget-data`);
      const d = await r.json();
      if (!r.ok && !d.signedIn) {
        state.data = null;
        state.error = "signin";
      } else if (!r.ok) {
        state.data = null;
        state.error = d.error || "Unable to load rewards.";
      } else {
        state.data = d;
        if (d.settings) state.settings = { ...state.settings, ...d.settings };
        state.error = null;
      }
    } catch {
      state.error = "Unable to connect to rewards. Please try again.";
      state.data = null;
    }
    state.loading = false;
    render();
  }

  function triggerConfetti(originX, originY) {
    if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    let canvas = document.getElementById(`${PREFIX}-confetti-canvas`);
    if (!canvas) {
      canvas = document.createElement("canvas");
      canvas.id = `${PREFIX}-confetti-canvas`;
      document.body.appendChild(canvas);
    }
    const ctx = canvas.getContext("2d");
    const dpr = window.devicePixelRatio || 1;
    canvas.width = window.innerWidth * dpr;
    canvas.height = window.innerHeight * dpr;
    ctx.scale(dpr, dpr);

    const colors = ["#6F263D", "#D4A017", "#F59E0B", "#FAF9F7", "#8E2B4B", "#171717"];
    const startX = originX || window.innerWidth * 0.8;
    const startY = originY || window.innerHeight * 0.35;
    const count = 65;
    const particles = [];

    for (let i = 0; i < count; i++) {
      const angle = (Math.PI * 2 * i) / count + (Math.random() - 0.5) * 0.5;
      const speed = 4 + Math.random() * 9;
      particles.push({
        x: startX,
        y: startY,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed - 3.5,
        size: 5 + Math.random() * 6,
        color: colors[Math.floor(Math.random() * colors.length)],
        rotation: Math.random() * 360,
        rotationSpeed: (Math.random() - 0.5) * 14,
        opacity: 1,
        gravity: 0.22,
        drag: 0.94
      });
    }

    let start = null;
    function frame(t) {
      if (!start) start = t;
      const elapsed = t - start;
      ctx.clearRect(0, 0, window.innerWidth, window.innerHeight);

      let alive = false;
      for (const p of particles) {
        p.vx *= p.drag;
        p.vy *= p.drag;
        p.vy += p.gravity;
        p.x += p.vx;
        p.y += p.vy;
        p.rotation += p.rotationSpeed;
        p.opacity = Math.max(0, 1 - elapsed / 1900);

        if (p.opacity > 0) {
          alive = true;
          ctx.save();
          ctx.translate(p.x, p.y);
          ctx.rotate((p.rotation * Math.PI) / 180);
          ctx.globalAlpha = p.opacity;
          ctx.fillStyle = p.color;
          ctx.fillRect(-p.size / 2, -p.size / 2, p.size, p.size * 0.65);
          ctx.restore();
        }
      }

      if (alive && elapsed < 2000) {
        requestAnimationFrame(frame);
      } else {
        ctx.clearRect(0, 0, window.innerWidth, window.innerHeight);
        if (canvas && canvas.parentNode) canvas.parentNode.removeChild(canvas);
      }
    }
    requestAnimationFrame(frame);
  }

  function renderSkeleton() {
    return `
      <div class="${PREFIX}-skeleton-wrap">
        <div class="${PREFIX}-skeleton-hero"></div>
        <div style="display:flex;gap:8px">
          <div class="${PREFIX}-skeleton-line" style="flex:1"></div>
          <div class="${PREFIX}-skeleton-line" style="flex:1"></div>
          <div class="${PREFIX}-skeleton-line" style="flex:1"></div>
        </div>
        <div class="${PREFIX}-skeleton-card" style="margin-top:12px"></div>
        <div class="${PREFIX}-skeleton-card"></div>
        <div class="${PREFIX}-skeleton-card"></div>
      </div>
    `;
  }

  async function handleRedeem(rewardId) {
    if (state.redeemingId) return;
    state.redeemingId = rewardId;
    render();
    try {
      const r = await fetch(PROXY, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ intent: "redeem", rewardId }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || "Redemption failed");
      if (d.code) {
        state.redeemedCodes[rewardId] = d.code;
        showToast("Reward redeemed! Discount code generated.");
        triggerConfetti();
      } else {
        showToast(d.message || "Store credit added to account!");
        triggerConfetti();
      }
      setTimeout(() => fetchData(), 500);
    } catch (e) {
      showToast(String(e?.message || "Could not redeem."));
    }
    state.redeemingId = null;
    render();
  }

  async function handleEnterDraw(drawId) {
    if (state.enteringDraw) return;
    state.enteringDraw = true;
    render();
    try {
      const r = await fetch(PROXY, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ intent: "enter_draw", drawId, marketingOptIn: true }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || "Failed to enter draw");
      if (d.alreadyEntered) {
        showToast("You're already entered in this drop!");
      } else {
        showToast(`🎉 You're in! Entry #${d.entryNumber || "confirmed"}`);
        triggerConfetti();
      }
    } catch (e) {
      showToast(String(e?.message || "Failed to enter."));
    }
    state.enteringDraw = false;
    render();
  }

  function toggle() {
    state.open = !state.open;
    if (state.open && !state.data && !state.loading) fetchData();
    render();
  }

  // ── Render Helpers ──
  function renderHero() {
    const d = state.data;
    if (!d) return "";
    const c = d.customer;
    const tierColor = TIER_COLORS[c.tier] || "#111";
    const prog = d.progress || {};

    let progressHtml = "";
    if (prog.nextTier) {
      progressHtml = `
        <div class="${PREFIX}-progress-wrap">
          <div class="${PREFIX}-progress-bar"><div class="${PREFIX}-progress-fill" style="width:${prog.progress || 0}%"></div></div>
          <div class="${PREFIX}-progress-label"><span>${pts(prog.progress)}%</span><span>${pts(prog.pointsNeeded)} pts to ${esc(prog.nextTier)}</span></div>
        </div>`;
    } else {
      progressHtml = `<div class="${PREFIX}-progress-label" style="justify-content:center;margin-top:6px"><span>🏆 Top tier reached!</span></div>`;
    }

    return `
      <div class="${PREFIX}-hero">
        <p class="${PREFIX}-hero-points">${pts(c.points)}</p>
        <p class="${PREFIX}-hero-label">${esc(state.settings?.pointsName || "Points")} available</p>
        ${c.pendingPoints > 0 ? `<p class="${PREFIX}-hero-pending">${pts(c.pendingPoints)} pending</p>` : ""}
        <span class="${PREFIX}-tier-badge" style="background:${tierColor}">${esc(c.tier)}</span>
        ${progressHtml}
      </div>`;
  }

  function renderRewards() {
    const d = state.data;
    if (!d) return "";
    const rewards = d.rewards || [];
    if (!rewards.length) return `<div class="${PREFIX}-section" style="text-align:center;color:#aaa">No rewards available right now.</div>`;

    return `<div class="${PREFIX}-section">` + rewards
      .map((r, idx) => {
        const canAfford = d.customer.points >= r.pointsCost;
        const isRedeeming = state.redeemingId === r.id;
        const redeemed = state.redeemedCodes[r.id];
        const tierLocked = r.tierRequired && TIER_ORDER.indexOf(d.customer.tier) < TIER_ORDER.indexOf(r.tierRequired);

        let btnHtml = "";
        if (redeemed) {
          btnHtml = "";
        } else if (tierLocked) {
          btnHtml = `<button class="${PREFIX}-reward-btn ${PREFIX}-reward-btn-locked" disabled>${esc(r.tierRequired)} only</button>`;
        } else if (!canAfford) {
          btnHtml = `<button class="${PREFIX}-reward-btn ${PREFIX}-reward-btn-locked" disabled>Need ${pts(r.pointsCost - d.customer.points)} more</button>`;
        } else {
          btnHtml = `<button class="${PREFIX}-reward-btn ${PREFIX}-reward-btn-redeem" data-redeem="${esc(r.id)}" ${isRedeeming ? "disabled" : ""}>${isRedeeming ? "Redeeming…" : "Redeem"}</button>`;
        }

        const codeHtml = redeemed
          ? `<div class="${PREFIX}-code-box"><span class="${PREFIX}-code-text">${esc(redeemed)}</span><button class="${PREFIX}-code-copy" data-copy-code="${esc(redeemed)}" data-copy-key="code-${esc(r.id)}">${state.copiedKey === `code-${r.id}` ? "Copied!" : "Copy"}</button></div>`
          : "";

        return `
          <div class="${PREFIX}-reward-card ${PREFIX}-card-anim" style="animation-delay:${idx * 45}ms">
            <div class="${PREFIX}-reward-info">
              <p class="${PREFIX}-reward-name">${esc(r.name)}</p>
              ${r.description ? `<p class="${PREFIX}-reward-desc">${esc(r.description)}</p>` : ""}
              <p class="${PREFIX}-reward-cost">${pts(r.pointsCost)} ${esc(state.settings?.pointsName || "pts")}</p>
              ${codeHtml}
            </div>
            ${btnHtml}
          </div>`;
      })
      .join("") + `</div>`;
  }

  function renderChallenges() {
    const d = state.data;
    if (!d) return "";
    const chs = d.challenges || [];
    if (!chs.length) return `<div class="${PREFIX}-section" style="text-align:center;color:#aaa">No active challenges right now.</div>`;

    return `<div class="${PREFIX}-section">` + chs.map((c, idx) => `
      <div class="${PREFIX}-card ${PREFIX}-card-anim" style="animation-delay:${idx * 45}ms">
        <p class="${PREFIX}-card-title">${esc(c.name)}</p>
        <p class="${PREFIX}-card-desc">${esc(c.description || `Target: ${c.targetValue} (${c.challengeType})`)}</p>
        <span class="${PREFIX}-card-badge">+${pts(c.rewardPoints)} points</span>
      </div>
    `).join("") + `</div>`;
  }

  function renderDraw() {
    const d = state.data;
    if (!d || !d.currentDraw) return `<div class="${PREFIX}-section" style="text-align:center;color:#aaa">No prize drop is open right now. Check back soon!</div>`;
    const draw = d.currentDraw;

    return `
      <div class="${PREFIX}-section ${PREFIX}-card-anim">
        <div class="${PREFIX}-draw-box">
          <span style="font-size:11px;font-weight:700;letter-spacing:.08em;color:#d4a017">EXCLUSIVE DROP</span>
          <h3 style="margin:6px 0 4px;font-size:16px">${esc(draw.publicName)}</h3>
          <p style="font-size:12px;color:#666;margin:0 0 12px">Free member entry · ${esc(draw.territory || "UK")}</p>
          ${(draw.prizes || []).map(p => `<div style="font-size:13px;font-weight:600;padding:4px 0">🎁 ${esc(p.title)}</div>`).join("")}
          <button class="${PREFIX}-draw-btn" data-enter-draw="${esc(draw.id)}" ${state.enteringDraw ? "disabled" : ""}>
            ${state.enteringDraw ? "Entering…" : "ENTER FREE DROP"}
          </button>
        </div>
      </div>
    `;
  }

  function renderActivity() {
    const d = state.data;
    if (!d) return "";
    const tx = d.transactions || [];
    if (!tx.length) return `<div class="${PREFIX}-section" style="text-align:center;color:#aaa">No activity yet. Earn points with orders!</div>`;

    return `<div class="${PREFIX}-section">` + tx
      .map(
        (t, idx) => `
        <div class="${PREFIX}-tx-row ${PREFIX}-card-anim" style="animation-delay:${idx * 35}ms">
          <div style="flex:1;min-width:0">
            <div style="font-weight:550;white-space:nowrap;overflow:hidden;text-overflow:ellipsis">${esc(t.description)}</div>
            <div style="font-size:11px;color:#aaa">${formatDate(t.createdAt)}</div>
          </div>
          <span class="${PREFIX}-tx-pts ${t.points > 0 ? `${PREFIX}-pos` : `${PREFIX}-neg`}">${t.points > 0 ? "+" : ""}${pts(t.points)}</span>
        </div>`
      )
      .join("") + `</div>`;
  }

  function renderWallet() {
    const d = state.data;
    if (!d) return "";
    const active = d.wallet?.activeRewards || [];
    if (!active.length) return `<div class="${PREFIX}-section" style="text-align:center;color:#aaa">No active reward codes yet.</div>`;

    return `<div class="${PREFIX}-section">` + active
      .map(
        (r, idx) => `
        <div class="${PREFIX}-code-box ${PREFIX}-card-anim" style="margin-bottom:8px;animation-delay:${idx * 45}ms">
          <div>
            <div style="font-size:11px;color:#888">${esc(r.rewardName || "Reward")}</div>
            <div class="${PREFIX}-code-text">${esc(r.code)}</div>
          </div>
          <button class="${PREFIX}-code-copy ${state.copiedKey === `wallet-${r.code}` ? `${PREFIX}-copied` : ""}" data-copy-code="${esc(r.code)}" data-copy-key="wallet-${esc(r.code)}">
            ${state.copiedKey === `wallet-${r.code}` ? "Copied!" : "Copy"}
          </button>
        </div>`
      )
      .join("") + `</div>`;
  }

  function renderReferral() {
    const d = state.data;
    if (!d) return "";
    const code = d.customer.referralCode;
    if (!code) return `<div class="${PREFIX}-section" style="text-align:center;color:#aaa">Referral code unlocks after your first purchase.</div>`;

    return `
      <div class="${PREFIX}-section ${PREFIX}-card-anim">
        <div class="${PREFIX}-referral-box">
          <div style="font-size:12px;opacity:.75">Share your code with friends</div>
          <div class="${PREFIX}-referral-code">${esc(code)}</div>
          <button class="${PREFIX}-referral-copy" data-copy-code="${esc(code)}" data-copy-key="referral">
            ${state.copiedKey === "referral" ? "Copied!" : "Copy Code"}
          </button>
        </div>
      </div>`;
  }

  function render() {
    const launcher = document.getElementById(`${PREFIX}-launcher`);
    const overlay = document.getElementById(`${PREFIX}-overlay`);
    const panel = document.getElementById(`${PREFIX}-panel`);
    if (!launcher || !panel) return;

    const badge = launcher.querySelector(`.${PREFIX}-launcher-badge`);
    if (state.data && state.data.customer && state.data.customer.points > 0) {
      if (badge) {
        badge.textContent = pts(state.data.customer.points);
        badge.style.display = "";
      }
    } else if (badge) {
      badge.style.display = "none";
    }

    if (state.open) {
      overlay.classList.add(`${PREFIX}-open`);
      panel.classList.add(`${PREFIX}-open`);
      document.body.style.overflow = "hidden";
    } else {
      overlay.classList.remove(`${PREFIX}-open`);
      panel.classList.remove(`${PREFIX}-open`);
      document.body.style.overflow = "";
    }

    const body = panel.querySelector(`.${PREFIX}-body`);
    if (!body) return;

    if (state.loading) {
      body.innerHTML = renderSkeleton();
      return;
    }

    if (state.error === "signin") {
      body.innerHTML = `<div style="text-align:center;padding:48px 22px;color:#666" class="${PREFIX}-card-anim">
        <p style="font-size:32px;margin:0 0 12px">🎁</p>
        <p style="font-size:15px;font-weight:700;color:#171717;margin:0 0 8px">Join or Sign In</p>
        <p style="font-size:13px;line-height:1.5;margin:0 0 18px">Sign in to your account to view your points balance, redeem rewards, and enter weekly prize drops.</p>
        <p><a href="/account/login" style="display:inline-block;padding:10px 22px;background:#6F263D;color:#fff;border-radius:7px;text-decoration:none;font-weight:700;font-size:13px">Sign in to Account</a></p>
      </div>`;
      return;
    }

    if (state.error) {
      body.innerHTML = `<div style="text-align:center;padding:36px;color:#dc2626" class="${PREFIX}-card-anim">${esc(state.error)}</div>`;
      return;
    }

    if (!state.data) {
      body.innerHTML = renderSkeleton();
      return;
    }

    const tabs = [
      { key: "rewards", label: "Rewards" },
      { key: "challenges", label: "Missions" },
      { key: "draw", label: "Drops" },
      { key: "wallet", label: "Codes" },
      { key: "activity", label: "Activity" },
      { key: "referral", label: "Refer" }
    ];

    let content = "";
    if (state.tab === "rewards") content = renderRewards();
    else if (state.tab === "challenges") content = renderChallenges();
    else if (state.tab === "draw") content = renderDraw();
    else if (state.tab === "wallet") content = renderWallet();
    else if (state.tab === "activity") content = renderActivity();
    else if (state.tab === "referral") content = renderReferral();

    body.innerHTML = `
      ${renderHero()}
      <div class="${PREFIX}-tabs">
        ${tabs.map((t) => `<button class="${PREFIX}-tab ${state.tab === t.key ? `${PREFIX}-active` : ""}" data-tab="${t.key}">${t.label}</button>`).join("")}
      </div>
      ${content}
    `;
  }

  function handleClick(e) {
    const target = e.target.closest("button[data-redeem], button[data-enter-draw], button[data-copy-code], button[data-tab]");
    if (!target) return;

    if (target.dataset.redeem) {
      e.preventDefault();
      handleRedeem(target.dataset.redeem);
    }
    if (target.dataset.enterDraw) {
      e.preventDefault();
      handleEnterDraw(target.dataset.enterDraw);
    }
    if (target.dataset.copyCode) {
      e.preventDefault();
      copyText(target.dataset.copyCode, target.dataset.copyKey || "code");
    }
    if (target.dataset.tab) {
      e.preventDefault();
      state.tab = target.dataset.tab;
      render();
    }
  }

  function createWidget() {
    const launcher = document.createElement("button");
    launcher.id = `${PREFIX}-launcher`;
    launcher.className = `${PREFIX}-launcher`;
    launcher.setAttribute("aria-label", "Open Nexsus+ Rewards");
    launcher.innerHTML = `
      <span class="${PREFIX}-launcher-icon" aria-hidden="true">🎁</span>
      <span class="${PREFIX}-launcher-text">Rewards</span>
      <span class="${PREFIX}-launcher-badge" style="display:none">0</span>
    `;
    launcher.addEventListener("click", toggle);

    const overlay = document.createElement("div");
    overlay.id = `${PREFIX}-overlay`;
    overlay.className = `${PREFIX}-overlay`;
    overlay.addEventListener("click", toggle);

    const panel = document.createElement("div");
    panel.id = `${PREFIX}-panel`;
    panel.className = `${PREFIX}-panel`;
    panel.innerHTML = `
      <div class="${PREFIX}-panel-head">
        <h2><span>🎁</span> Nexsus+ Rewards</h2>
        <button class="${PREFIX}-close" aria-label="Close rewards">&times;</button>
      </div>
      <div class="${PREFIX}-body"></div>
    `;
    panel.querySelector(`.${PREFIX}-close`).addEventListener("click", toggle);
    panel.addEventListener("click", handleClick);

    const bodyEl = panel.querySelector(`.${PREFIX}-body`);
    const headEl = panel.querySelector(`.${PREFIX}-panel-head`);
    if (bodyEl && headEl) {
      bodyEl.addEventListener("scroll", () => {
        if (bodyEl.scrollTop > 8) {
          headEl.classList.add(`${PREFIX}-scrolled`);
        } else {
          headEl.classList.remove(`${PREFIX}-scrolled`);
        }
      }, { passive: true });
    }

    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && state.open) toggle();
    });

    document.body.appendChild(launcher);
    document.body.appendChild(overlay);
    document.body.appendChild(panel);
  }

  // ── Product Page Messaging ──
  function setupProductPoints() {
    if (state.settings.productPointsEnabled === false) return;

    // Check if on product page
    const isProductPage =
      window.location.pathname.includes("/products/") ||
      document.querySelector('meta[property="og:type"]')?.content === "product" ||
      Boolean(document.querySelector('[data-np-price], .np-product-detail, form[action*="/cart/add"]'));
    if (!isProductPage) return;

    const rate = Number(state.settings.pointsPerPound || 10);
    const name = state.settings.pointsName || "Nexsus Points";

    function parsePrice(text) {
      if (!text) return 0;
      const clean = String(text).replace(/[^0-9.]/g, "");
      return parseFloat(clean) || 0;
    }

    function getPrice() {
      // 1. Check theme-specific [data-np-price]
      const npPrice = document.querySelector('[data-np-price], .np-product-price');
      if (npPrice && npPrice.textContent) {
        const p = parsePrice(npPrice.textContent);
        if (p > 0) return p;
      }
      // 2. Standard selectors
      const selectors = [
        ".price-item--regular",
        ".price__regular .price-item",
        "[data-product-price]",
        ".product__price",
        ".product-price",
        ".price"
      ];
      for (const s of selectors) {
        const el = document.querySelector(s);
        if (el && el.textContent) {
          const p = parsePrice(el.textContent);
          if (p > 0) return p;
        }
      }
      return 0;
    }

    function injectOrUpdate() {
      const price = getPrice();
      if (price <= 0) return;
      const points = Math.floor(price * rate);
      if (points <= 0) return;

      let badge = document.getElementById(`${PREFIX}-product-badge`);
      if (!badge) {
        const target =
          document.querySelector('[data-np-price]') ||
          document.querySelector('.np-product-price') ||
          document.querySelector('.np-product-compare-price') ||
          document.querySelector('.price') ||
          document.querySelector('.np-add-button') ||
          document.querySelector('form[action*="/cart/add"]') ||
          document.querySelector('.product-form');

        if (!target) return;

        badge = document.createElement("div");
        badge.id = `${PREFIX}-product-badge`;
        badge.className = `${PREFIX}-product-pill`;
        badge.setAttribute("role", "button");
        badge.setAttribute("title", "Click to view rewards & tiers");
        badge.addEventListener("click", () => {
          if (!state.open) toggle();
        });

        if (target.matches('[data-np-price], .np-product-price, .np-product-compare-price, .price')) {
          target.parentNode.insertBefore(badge, target.nextSibling);
        } else {
          target.parentNode.insertBefore(badge, target);
        }
      }

      badge.innerHTML = `
        <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>
        <span>Earn <strong>${pts(points)} ${esc(name)}</strong> with this item</span>
      `;
    }

    setTimeout(injectOrUpdate, 300);
    setTimeout(injectOrUpdate, 1000);
    setTimeout(injectOrUpdate, 2500);

    // Watch for variant/price changes
    const priceNode = document.querySelector('[data-np-price], .np-product-price, .price');
    if (priceNode && window.MutationObserver) {
      const obs = new MutationObserver(() => injectOrUpdate());
      obs.observe(priceNode, { childList: true, characterData: true, subtree: true });
    }
  }

  // ── Cart Page / Drawer Messaging ──
  function setupCartPoints() {
    if (state.settings.cartPointsEnabled === false) return;

    const rate = Number(state.settings.pointsPerPound || 10);
    const name = state.settings.pointsName || "Nexsus Points";

    async function checkCart() {
      try {
        const r = await fetch("/cart.js");
        if (!r.ok) return;
        const cart = await r.json();
        const total = (cart.total_price || 0) / 100;
        const points = Math.floor(total * rate);

        const existingBanners = document.querySelectorAll(`.${PREFIX}-cart-banner`);
        if (points <= 0) {
          existingBanners.forEach((b) => b.remove());
          return;
        }

        const targets = document.querySelectorAll(
          '.np-cart-shell, .np-cart-page, .np-cart-summary, form[action*="/cart"], .cart__footer, #cart-drawer, .cart-drawer'
        );

        targets.forEach((container) => {
          let banner = container.querySelector(`.${PREFIX}-cart-banner`);
          if (!banner) {
            banner = document.createElement("div");
            banner.className = `${PREFIX}-cart-banner`;
            const checkoutBtn = container.querySelector('[name="checkout"], .np-cart-checkout, button[type="submit"]');
            if (checkoutBtn) {
              checkoutBtn.parentNode.insertBefore(banner, checkoutBtn);
            } else {
              container.prepend(banner);
            }
          }
          banner.innerHTML = `
            <span style="font-size:20px;line-height:1">🎁</span>
            <div>
              <div>You'll earn <strong>${pts(points)} ${esc(name)}</strong> on this order!</div>
              <div style="font-size:11px;color:#6B6B6B;margin-top:2px">Redeemable for instant discounts on future orders.</div>
            </div>
          `;
        });
      } catch {}
    }

    if (window.location.pathname.includes("/cart") || document.querySelector('.np-cart-page, .cart')) {
      setTimeout(checkCart, 400);
      setTimeout(checkCart, 1200);
    }

    document.addEventListener("change", (e) => {
      if (e.target.closest('form[action*="/cart"], .np-cart-page, .cart')) {
        setTimeout(checkCart, 500);
      }
    });

    // Listen to fetch calls to /cart/add.js to catch drawer additions
    const origFetch = window.fetch;
    window.fetch = function () {
      const url = arguments[0];
      const res = origFetch.apply(this, arguments);
      if (typeof url === "string" && (url.includes("/cart/add") || url.includes("/cart/change") || url.includes("/cart/clear"))) {
        setTimeout(checkCart, 600);
      }
      return res;
    };
  }

  async function init() {
    if (window.location.pathname.startsWith("/admin")) return;
    injectStyles();
    createWidget();
    await fetchSettings();
    setupProductPoints();
    setupCartPoints();
    render();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
